import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Coins, Loader2, Settings2, Sparkles, Square, Upload, UserRound, Check, X, ImagePlus } from 'lucide-react';
import { useGraphStore } from '../../store/graphStore';
import { KIND_META } from './nodes';
import GeneratorControls from './GeneratorControls';
import ResultsStrip from './ResultsStrip';
import { fileUrl } from '../../lib/api';
import { uploadAssetFile } from '../../lib/assetUpload';
import { toast } from '../../hooks/use-toast';

// Modo Simple: la vista con la que abre una sesion que viene de una receta.
// El 90% llega con todo ya decidido por la receta; lo unico que de verdad
// aporta el usuario es su foto y cuantas tomas quiere. Todo lee y escribe el
// mismo grafo del graphStore, asi que el canvas siempre esta sincronizado.

// Tipos de nodo de asset que en el grafo de una receta representan "la foto
// que trae el usuario". Environment queda fuera: en las recetas es texto
// descrito, no una imagen.
const SLOT_KINDS = ['product', 'model', 'outfit'];

function AssetSlot({ node, assets, onAssetUploaded }) {
  const updateNodeData = useGraphStore((s) => s.updateNodeData);
  const d = node.data || {};
  const meta = KIND_META[d.kind] || KIND_META.product;
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [picking, setPicking] = useState(false);
  // The asset behind the thumb can be deleted from the library while the
  // session still points at it; a broken <img> said nothing. onError clears
  // the dead thumb and the slot asks for a new photo instead.
  const [thumbDead, setThumbDead] = useState(false);
  const sameKind = assets.filter((a) => a.type === d.kind);

  const applyAsset = (a) => {
    setThumbDead(false);
    updateNodeData(node.id, { assetId: a.id, name: a.name, thumb: a.url, kind: d.kind });
    setPicking(false);
  };

  const onThumbDead = () => {
    setThumbDead(true);
    updateNodeData(node.id, { thumb: null });
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const a = await uploadAssetFile(file, d.kind);
      toast({ title: 'Asset uploaded', description: 'Added to your library.' });
      if (onAssetUploaded) await onAssetUploaded();
      applyAsset(a);
    } catch (err) {
      toast({ title: 'Upload failed', description: err.message });
    }
    setUploading(false);
  };

  return (
    <div className="rounded-xl border border-[#2A2A30] bg-[#141417] p-3" data-testid={`simple-slot-${node.id}`}>
      <div className="flex items-center gap-3">
        <div className="h-20 w-20 shrink-0 overflow-hidden rounded-lg border border-[#2A2A30] bg-[#0E0E10]">
          {d.thumb ? (
            <img src={fileUrl(d.thumb)} alt="" onError={onThumbDead} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <ImagePlus size={20} className="text-white/25" />
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: meta.color }}>{meta.label}</p>
          <p className="mt-0.5 truncate text-sm text-white/85">{thumbDead ? 'No photo yet' : (d.name || 'No photo yet')}</p>
          {thumbDead ? (
            <p className="mt-0.5 text-[10px] text-amber-300/80">The original photo is gone — add another</p>
          ) : !d.assetId && (
            <p className="mt-0.5 text-[10px] text-white/40">The recipe describes a stand-in; add your real photo to replace it.</p>
          )}
        </div>
        <div className="flex shrink-0 flex-col gap-1.5">
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid={`simple-slot-upload-${node.id}`}
            className="inline-flex items-center gap-1.5 rounded-md border border-[#2A2A30] bg-[#0E0E10] px-2.5 py-1.5 text-xs text-white/80 transition-colors hover:border-white/30 disabled:opacity-50"
          >
            {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Upload
          </button>
          {sameKind.length > 0 && (
            <button
              onClick={() => setPicking((v) => !v)}
              data-testid={`simple-slot-pick-${node.id}`}
              className="inline-flex items-center gap-1.5 rounded-md border border-[#2A2A30] bg-[#0E0E10] px-2.5 py-1.5 text-xs text-white/80 transition-colors hover:border-white/30"
            >
              {picking ? <X size={12} /> : <ImagePlus size={12} />} Library
            </button>
          )}
        </div>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onUpload} />
      </div>
      {picking && (
        <div className="mt-3 grid grid-cols-5 gap-2 border-t border-[#2A2A30] pt-3">
          {sameKind.map((a) => (
            <button key={a.id} onClick={() => applyAsset(a)} title={a.name}
              data-testid={`simple-slot-asset-${a.id}`}
              className={`aspect-square overflow-hidden rounded-lg border-2 transition-all ${
                d.assetId === a.id ? 'border-white ring-1 ring-white/40' : 'border-[#2A2A30] hover:border-white/40'}`}>
              {a.url && <img src={`${process.env.REACT_APP_BACKEND_URL}${a.url}`} alt="" className="h-full w-full object-cover" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function InfluencerSlot({ node, influencers }) {
  const updateNodeData = useGraphStore((s) => s.updateNodeData);
  const d = node.data || {};
  const [picking, setPicking] = useState(false);
  return (
    <div className="rounded-xl border border-[#2A2A30] bg-[#141417] p-3" data-testid={`simple-slot-${node.id}`}>
      <div className="flex items-center gap-3">
        <div className="h-20 w-20 shrink-0 overflow-hidden rounded-lg border border-[#2A2A30] bg-[#0E0E10]">
          {d.thumb ? (
            <img src={fileUrl(d.thumb)} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center"><UserRound size={20} className="text-white/25" /></div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-node-model">Influencer</p>
          <p className="mt-0.5 truncate text-sm text-white/85">{d.name || 'Pick a person'}</p>
          <p className="mt-0.5 text-[10px] text-white/40">Same face across every render.</p>
        </div>
        {influencers.length > 0 && (
          <button onClick={() => setPicking((v) => !v)}
            data-testid={`simple-slot-pick-${node.id}`}
            className="shrink-0 rounded-md border border-[#2A2A30] bg-[#0E0E10] px-2.5 py-1.5 text-xs text-white/80 transition-colors hover:border-white/30">
            {picking ? 'Close' : 'Change'}
          </button>
        )}
      </div>
      {picking && (
        <div className="mt-3 grid grid-cols-5 gap-2 border-t border-[#2A2A30] pt-3">
          {influencers.map((inf) => (
            <button key={inf.id} title={inf.name}
              onClick={() => { updateNodeData(node.id, { influencerId: inf.id, name: inf.name, thumb: inf.cover_url, persona: inf.persona }); setPicking(false); }}
              data-testid={`simple-slot-influencer-${inf.id}`}
              className={`aspect-square overflow-hidden rounded-lg border-2 transition-all ${
                d.influencerId === inf.id ? 'border-node-model ring-1 ring-node-model/50' : 'border-[#2A2A30] hover:border-white/40'}`}>
              {inf.cover_url
                ? <img src={`${process.env.REACT_APP_BACKEND_URL}${inf.cover_url}`} alt="" className="h-full w-full object-cover" />
                : <div className="flex h-full w-full items-center justify-center bg-[#0E0E10]"><UserRound size={14} className="text-white/30" /></div>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SimpleMode({
  wf, assets, influencers, onAssetUploaded, generations,
  generating, genCost, compiled, onGenerate, onStop, onOpenCanvas, onMakeVideo,
  saving, user,
}) {
  const navigate = useNavigate();
  const { nodes, updateNodeData, setSelected } = useGraphStore();

  const gen = useMemo(() => nodes.find((n) => n.type === 'generator.image') || null, [nodes]);

  // Con el generador seleccionado, el compile en vivo del Studio (y con el su
  // coste estimado) funciona igual que en el canvas: es el mismo efecto.
  useEffect(() => {
    if (gen) setSelected(gen.id);
    // eslint-disable-next-line
  }, [gen?.id]);

  const slots = useMemo(() => nodes.filter(
    (n) => n.type?.startsWith('asset.') && SLOT_KINDS.includes(n.data?.kind),
  ), [nodes]);
  const infSlots = useMemo(() => nodes.filter((n) => n.type === 'asset.influencer'), [nodes]);
  const totalSlots = slots.length + infSlots.length;

  const d = gen?.data || {};
  const set = (patch) => gen && updateNodeData(gen.id, patch);

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto" data-testid="simple-mode">
      <div className="relative mx-auto w-full max-w-2xl px-4 py-8">
        {/* Enlace discreto al canvas, en la esquina. */}
        <button
          onClick={onOpenCanvas}
          data-testid="simple-open-canvas"
          className="absolute right-4 top-4 inline-flex items-center gap-1.5 rounded-full border border-[#2A2A30] px-3 py-1.5 text-xs text-white/50 transition-colors hover:border-white/30 hover:text-white"
        >
          <Settings2 size={13} /> Open canvas
        </button>

        <div className="mb-8 flex items-start gap-3 pr-32">
          <button onClick={() => navigate('/app')} className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-white/60 hover:bg-white/5">
            <ArrowLeft size={16} />
          </button>
          <div className="min-w-0">
            <h1 className="font-serif-display truncate text-2xl text-white">{wf?.name || 'Untitled session'}</h1>
            <div className="mt-1 flex items-center gap-2 text-xs text-white/45">
              {wf?.recipe_label && (
                <span className="rounded-full border border-[#2A2A30] bg-[#141417] px-2.5 py-0.5">{wf.recipe_label}</span>
              )}
              <span className="flex items-center gap-1">
                {saving ? <><Loader2 size={11} className="animate-spin" /> Saving…</> : <><Check size={11} className="text-node-environment" /> Saved</>}
              </span>
            </div>
          </div>
        </div>

        {/* Paso 1: la(s) foto(s) */}
        <section className="mb-8">
          <h2 className="mb-3 flex items-baseline gap-2">
            <span className="font-serif-display text-lg text-white">1 · The photo{totalSlots !== 1 ? 's' : ''}</span>
            <span className="text-xs text-white/40">what only you can bring</span>
          </h2>
          <div className="space-y-3">
            {infSlots.map((n) => <InfluencerSlot key={n.id} node={n} influencers={influencers} />)}
            {slots.map((n) => <AssetSlot key={n.id} node={n} assets={assets} onAssetUploaded={onAssetUploaded} />)}
            {totalSlots === 0 && (
              <p className="rounded-xl border border-dashed border-[#2A2A30] p-4 text-center text-xs text-white/40">
                This recipe needs no photo of yours — everything is already described.
              </p>
            )}
          </div>
        </section>

        {/* Paso 2: ajustes. Los mismos controles compartidos del panel derecho,
            escribiendo sobre el mismo nodo generador del grafo. */}
        <section className="mb-8">
          <h2 className="mb-3 flex items-baseline gap-2">
            <span className="font-serif-display text-lg text-white">2 · Settings</span>
            <span className="text-xs text-white/40">the recipe decided the rest</span>
          </h2>
          {gen ? (
            <div className="space-y-4 rounded-xl border border-[#2A2A30] bg-[#141417] p-4">
              <GeneratorControls d={d} set={set} compiled={compiled} />
            </div>
          ) : (
            <p className="rounded-xl border border-dashed border-[#2A2A30] p-4 text-center text-xs text-white/40">
              This session has no image generator. Open the canvas to add one.
            </p>
          )}
        </section>

        {/* Generar: exactamente el mismo handler que el boton del canvas. */}
        <div className="mb-2 flex items-center gap-3">
          <button
            onClick={onGenerate}
            disabled={generating || !gen}
            data-testid="simple-generate-btn"
            className="inline-flex h-12 flex-1 items-center justify-center gap-2 rounded-full bg-white text-base font-medium text-black transition-transform hover:scale-[1.01] disabled:opacity-50"
          >
            {generating ? <Loader2 size={17} className="animate-spin" /> : <Sparkles size={17} />}
            {generating ? 'Generating…' : (genCost ? `Generate · ${genCost} credits` : 'Generate')}
          </button>
          {generating && (
            <button onClick={onStop} data-testid="simple-stop-btn"
              className="inline-flex h-12 items-center gap-1.5 rounded-full border border-red-500/50 bg-red-500/10 px-5 text-sm font-medium text-red-300 transition-colors hover:bg-red-500/20">
              <Square size={13} className="fill-current" /> Stop
            </button>
          )}
        </div>
        <p className="mb-6 text-center text-[11px] text-white/35">
          <Coins size={11} className="mr-1 inline text-node-product" />
          {user?.role === 'admin' ? '∞' : (user?.credits_balance ?? 0)} credits available
        </p>
      </div>

      {/* Resultados: la misma tira que en el canvas. */}
      <div className="mx-auto w-full max-w-2xl px-4 pb-8">
        <ResultsStrip generations={generations} onMakeVideo={onMakeVideo} />
      </div>
    </div>
  );
}
